import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { ListTutorTask } from '@shared/model'
import type { TutorTasksFilterParams, TutorTasksFilters } from '../model/tutor-tasks/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { TaskActorType, TaskStatus, TaskType } from '@brighterly/lib-core-types/tutor/enums'

import dayjs from 'dayjs'

import { getFirstQueryValue, normalizeArrayQuery, toIdValue } from '@shared/lib/routes'

/** Array filters that links from app-main encode as `key[0]=…`. */
export const TUTOR_TASKS_ARRAY_QUERY_KEYS: readonly string[] = ['tutor', 'status', 'actor_type', 'type']

/** Contract: `query` is at most 255 characters. */
export const TUTOR_TASKS_SEARCH_MAX_LENGTH = 255

const FILTER_QUERY_KEYS: readonly string[] = ['query', 'tutor', 'status', 'actor_type', 'type', 'due_date_from', 'due_date_to']
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
const ACTOR_TYPES: readonly string[] = Object.values(TaskActorType)
const TASK_TYPES: readonly string[] = Object.values(TaskType)

const toDateTimeValue = (value: unknown): string | null => {
  const raw = getFirstQueryValue(value)
  return raw !== null && DATE_TIME_PATTERN.test(raw) && dayjs(raw).isValid() ? raw : null
}

const isId = (value: number | null): value is number => value !== null

const isOpenStatus = (value: string): value is TaskStatus =>
  value === TaskStatus.Planned || value === TaskStatus.Completed

export const isTaskActorType = (value: string): value is TaskActorType => ACTOR_TYPES.includes(value)

export const isTaskType = (value: string): value is TaskType => TASK_TYPES.includes(value)

export const emptyTutorTasksFilters = (): TutorTasksFilters => ({
  search: null,
  tutorIds: [],
  statuses: [],
  actorTypes: [],
  types: [],
  dueDateFrom: null,
  dueDateTo: null,
})

export const queryToTutorTasksFilters = (query: Record<string, unknown>, lists: ListTutorTask | null): TutorTasksFilters => {
  const knownTypes = (normalizeArrayQuery(query.type) ?? []).filter(isTaskType)
  const dueDateFrom = toDateTimeValue(query.due_date_from)
  // The legacy select offers "Canceled by …" only: a CANCELED status without an actor cannot be shown.
  const actorTypes = (normalizeArrayQuery(query.actor_type) ?? []).filter(isTaskActorType)

  return {
    search: getFirstQueryValue(query.query)?.slice(0, TUTOR_TASKS_SEARCH_MAX_LENGTH) ?? null,
    tutorIds: (normalizeArrayQuery(query.tutor) ?? []).map(toIdValue).filter(isId),
    statuses: (normalizeArrayQuery(query.status) ?? []).filter(isOpenStatus),
    actorTypes,
    types: lists ? knownTypes.filter(type => type in lists.type) : knownTypes,
    dueDateFrom,
    dueDateTo: dueDateFrom ? toDateTimeValue(query.due_date_to) : null,
  }
}

/** CANCELED joins the statuses whenever an actor is picked: the API narrows only that branch by actor. */
export const getTutorTasksFilterParams = (filters: TutorTasksFilters): TutorTasksFilterParams => {
  const statuses: string[] = filters.actorTypes.length
    ? [...filters.statuses, TaskStatus.Canceled]
    : [...filters.statuses]

  return {
    query: filters.search || undefined,
    tutor: filters.tutorIds.length ? filters.tutorIds : undefined,
    status: statuses.length ? statuses : undefined,
    actor_type: filters.actorTypes.length ? filters.actorTypes : undefined,
    type: filters.types.length ? filters.types : undefined,
    due_date_from: filters.dueDateFrom || undefined,
    due_date_to: filters.dueDateTo || undefined,
  }
}

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const tutorTasksFiltersToQuery = (filters: TutorTasksFilters): LocationQueryRaw => {
  const params = getTutorTasksFilterParams(filters)

  return {
    ...params,
    tutor: params.tutor?.map(String),
  }
}

const toComparable = (value: unknown): string => JSON.stringify(normalizeArrayQuery(value) ?? [])

export const getCanonicalTutorTasksQuery = (query: LocationQuery, filters: TutorTasksFilters): LocationQueryRaw | null => {
  const canonical = tutorTasksFiltersToQuery(filters)
  const isCanonical = FILTER_QUERY_KEYS.every(key => toComparable(query[key]) === toComparable(canonical[key]))

  return isCanonical ? null : { ...query, ...canonical }
}

export const toTutorTasksDateRange = (from: string | null, to: string | null): Date[] | null => {
  const fromDate = from ? dayjs(from) : null
  const toDate = to ? dayjs(to) : null

  if (!fromDate?.isValid()) return null

  return toDate?.isValid() ? [fromDate.toDate(), toDate.toDate()] : [fromDate.toDate()]
}

export const fromTutorTasksDateRange = (
  value: (Date | null | undefined)[] | null,
): Pick<TutorTasksFilters, 'dueDateFrom' | 'dueDateTo'> => {
  const toBound = (date: Date | null | undefined): string | null =>
    date && dayjs(date).isValid() ? dayjs(date).format(DateFormat.Default) : null

  const dueDateFrom = toBound(value?.[0])

  return {
    dueDateFrom,
    dueDateTo: dueDateFrom ? toBound(value?.[1]) : null,
  }
}
